import { useState } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import type { MobilisationEvent } from '@hcc/types';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { ApiError } from '../../lib/api';
import { useArchivePerson, useDeletePerson } from '../../hooks/usePeople';
import { isArchived } from '../../routes/admin/person/summaryMetrics';

/**
 * The two "danger zone" actions on the person record that aren't GDPR
 * erasure (that's PseudonymisePanel, in its own file alongside this one):
 *   - ArchivePanel: reversible hide-from-live-views housekeeping.
 *   - DeletePanel: permanent hard delete for test / erroneous records.
 * Split out so PersonEdit doesn't grow further past the file-size cap.
 */

export function ArchivePanel({
  personId,
  events,
  onChange,
}: {
  personId: string;
  events: MobilisationEvent[] | null;
  onChange: () => void;
}) {
  const archived = isArchived(events);
  const archive = useArchivePerson(personId);
  const [open, setOpen] = useState(false);

  async function run(reason?: string) {
    try {
      await archive.mutateAsync({ archive: !archived, reason });
      toast.success(archived ? 'Record unarchived' : 'Record archived');
      setOpen(false);
      onChange();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'archive failed');
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-warning/25 bg-warning-soft/15 shadow-sm">
      <div className="flex items-center gap-2 border-b border-warning/25 px-5 py-3">
        {archived ? (
          <ArchiveRestore className="h-4 w-4 text-warning-soft-fg" />
        ) : (
          <Archive className="h-4 w-4 text-warning-soft-fg" />
        )}
        <h2 className="text-sm font-semibold text-warning-soft-fg">
          {archived ? 'Archived' : 'Archive'}
        </h2>
      </div>
      <div className="p-5 sm:p-6">
        <p className="text-sm text-warning-soft-fg">
          {archived ? (
            <>
              This person is hidden from the live reception, station and coordinator views. They
              still appear in reports and exports. Unarchive to bring them back onto the day views.
            </>
          ) : (
            <>
              Hides this person from the live reception, station and coordinator views to keep the
              day uncluttered once they are fully onboarded. Nothing is deleted — they stay in
              reports and exports, and you can unarchive any time.
            </>
          )}
        </p>
      </div>
      <div className="flex justify-end border-t border-warning/25 px-5 py-3">
        <Button
          variant={archived ? 'secondary' : 'warning'}
          size="md"
          iconLeft={
            archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />
          }
          loading={archive.isPending}
          onClick={() => setOpen(true)}
        >
          {archived ? 'Unarchive this record' : 'Archive this record'}
        </Button>
      </div>

      <ConfirmModal
        open={open}
        tone="warning"
        icon={archived ? ArchiveRestore : Archive}
        title={archived ? 'Unarchive this record?' : 'Archive this record?'}
        message={
          archived
            ? 'The person reappears on the live reception, station and coordinator views.'
            : 'The person is hidden from the live views. Reports and exports are unaffected, and you can unarchive any time.'
        }
        reasonLabel="Note (optional, for the audit log)"
        reasonPlaceholder={
          archived ? 'e.g. returned for a second mobilisation' : 'e.g. onboarding complete, filed'
        }
        confirmLabel={archived ? 'Unarchive' : 'Archive'}
        cancelLabel="Cancel"
        onConfirm={run}
        onClose={() => setOpen(false)}
      />
    </section>
  );
}

/**
 * Permanent hard delete. Distinct from anonymise: this removes the
 * person and their entire event history for good. Intended for test or
 * erroneous records, never real attendees. Guarded by a type-the-name
 * confirmation so it can't be triggered by a stray click.
 */
export function DeletePanel({
  personId,
  fullName,
  onDone,
}: {
  personId: string;
  fullName: string;
  onDone: () => void;
}) {
  const del = useDeletePerson();
  const [confirmName, setConfirmName] = useState('');
  const matches = confirmName.trim() === fullName.trim();

  async function run() {
    if (!matches) return;
    try {
      await del.mutateAsync(personId);
      toast.success('Record permanently deleted');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'delete failed');
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border-2 border-danger/40 bg-danger-soft/15 shadow-sm">
      <div className="flex items-center gap-2 border-b border-danger/40 px-5 py-3">
        <Trash2 className="h-4 w-4 text-danger-soft-fg" />
        <h2 className="text-sm font-semibold text-danger-soft-fg">Delete permanently</h2>
      </div>
      <div className="space-y-3 p-5 sm:p-6">
        <p className="text-sm text-danger-soft-fg">
          Permanently removes this person and their entire signed event history.{' '}
          <strong>This cannot be undone and bypasses the audit trail.</strong> Use this only for
          test or erroneous records — to remove a real attendee for GDPR, use Anonymise above, which
          keeps the audit chain.
        </p>
        <label className="block text-sm text-danger-soft-fg">
          Type the person's full name (<strong>{fullName}</strong>) to confirm:
          <input
            type="text"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={fullName}
            className="mt-1 w-full rounded-token-sm border border-danger/40 bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-danger"
          />
        </label>
      </div>
      <div className="flex justify-end border-t border-danger/40 px-5 py-3">
        <Button
          variant="destructive"
          size="md"
          iconLeft={<Trash2 className="h-4 w-4" />}
          loading={del.isPending}
          disabled={!matches}
          onClick={run}
        >
          Delete permanently
        </Button>
      </div>
    </section>
  );
}
